// 知识树 SVG 可视化: 9 分支 × 4 阶梯 网格,带跨学科桥
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CURRICULUM, BRANCH_ORDER, LEVEL_ORDER, getCrossBridges, getPrereqs, getCurriculumStats
} from '../lib/curriculum.js';
import { useI18n, t } from '../lib/i18n.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

const LEVEL_INFO = {
  1: { name_zh: '火花', name_en: 'Spark',    icon: '🔥', color: 'fill-rose-500/30  stroke-rose-400' },
  2: { name_zh: '探究', name_en: 'Explorer', icon: '🔍', color: 'fill-amber-500/30  stroke-amber-400' },
  3: { name_zh: '实践', name_en: 'Solver',   icon: '⚙️', color: 'fill-emerald-500/30 stroke-emerald-400' },
  4: { name_zh: '创造', name_en: 'Creator',  icon: '🌟', color: 'fill-violet-500/30 stroke-violet-400' },
};

const CELL_W = 110;
const CELL_H = 56;
const COL_GAP = 32;
const ROW_GAP = 16;
const PADDING = 60;
const SVG_W = PADDING * 2 + CELL_W * 4 + COL_GAP * 3;
const SVG_H = PADDING * 2 + (CELL_H + ROW_GAP) * 9 - ROW_GAP + 80;

export default function KnowledgeTree() {
  const { lang } = useI18n();
  useDocumentTitle('🌳 ' + (lang === 'zh-CN' ? '知识树' : 'Knowledge Tree'));
  const nav = useNavigate();
  const isZh = lang === 'zh-CN';
  const [hover, setHover] = useState(null);
  const stats = getCurriculumStats();
  const prereqs = useMemo(() => getPrereqs(), []);
  const bridges = useMemo(() => getCrossBridges(), []);

  // 格子坐标
  const cellXY = (branchIdx, level) => ({
    x: PADDING + (level - 1) * (CELL_W + COL_GAP),
    y: PADDING + branchIdx * (CELL_H + ROW_GAP),
  });

  // 文字
  const labelOf = (b) => isZh ? CURRICULUM.branches[b].name_zh : CURRICULUM.branches[b].name_en;
  const levelLabel = (lv) => `L${lv}`;
  const countOf = (b, lv) => CURRICULUM.cells[`${b}.L${lv}`]?.count || 0;

  return (
    <div className="container-page py-8 max-w-[1400px] mx-auto">
      {/* 标题 */}
      <div className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold gradient-text mb-2">
          🌳 {isZh ? '知识树' : 'Knowledge Tree'}
        </h1>
        <p className="text-slate-400 text-sm">
          {isZh ? '9 大学科 × 4 阶梯,共 36 个知识 cell。悬停查看,点击进入。' : '9 disciplines × 4 levels = 36 cells. Hover for details, click to enter.'}
        </p>
      </div>

      {/* 图例 */}
      <div className="flex flex-wrap gap-3 justify-center mb-4 text-sm">
        {LEVEL_ORDER.map(lv => {
          const info = LEVEL_INFO[lv];
          return (
            <div key={lv} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <span className="text-base">{info.icon}</span>
              <span className="text-slate-200 font-medium">{isZh ? info.name_zh : info.name_en}</span>
              <span className="text-slate-500 font-mono text-xs">L{lv}</span>
            </div>
          );
        })}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="w-4 h-0.5 bg-rose-400/80" />
          <span className="text-slate-400 text-xs">{isZh ? '必修' : 'prereq'}</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="w-4 h-0.5 bg-cyan-400/80" style={{strokeDasharray: '3 3'}} />
          <span className="text-slate-400 text-xs">{isZh ? '跨学科' : 'bridge'}</span>
        </div>
      </div>

      <div className="card overflow-x-auto bg-slate-950/40 p-4">
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          width="100%"
          style={{ minWidth: 900, maxWidth: 1200, height: 'auto' }}
        >
          <defs>
            <marker id="arrow-prereq" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 z" fill="#f43f5e" opacity="0.7" />
            </marker>
            <marker id="arrow-bridge" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 z" fill="#22d3ee" opacity="0.6" />
            </marker>
            <linearGradient id="legend-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f43f5e" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
          </defs>

          {/* 顶部分支线 (L1 → L4 渐变) */}
          <line
            x1={PADDING} y1={PADDING - 30}
            x2={PADDING + 3 * CELL_W + 3 * COL_GAP + CELL_W} y2={PADDING - 30}
            stroke="url(#legend-grad)" strokeWidth="3" opacity="0.5"
          />
          <text x={PADDING} y={PADDING - 38} className="fill-slate-400 text-xs">
            {isZh ? '能力阶梯' : 'Mastery level'} →
          </text>
          {LEVEL_ORDER.map(lv => {
            const x = PADDING + (lv - 1) * (CELL_W + COL_GAP) + CELL_W / 2;
            return (
              <text key={lv} x={x} y={PADDING - 38} className="fill-slate-200 text-sm font-mono" textAnchor="middle">
                L{lv} {isZh ? LEVEL_INFO[lv].name_zh : LEVEL_INFO[lv].name_en}
              </text>
            );
          })}

          {/* 左侧分支标签 */}
          {BRANCH_ORDER.map((b, branchIdx) => {
            const y = PADDING + branchIdx * (CELL_H + ROW_GAP) + CELL_H / 2;
            return (
              <g key={b}>
                <text x={PADDING - 12} y={y + 5} className="fill-slate-300 text-sm font-medium" textAnchor="end">
                  {labelOf(b)}
                </text>
                <text x={PADDING - 12} y={y + 22} className="fill-slate-500 text-xs font-mono" textAnchor="end">
                  {stats.by_branch[b] || 0} {isZh ? '题' : 'qs'}
                </text>
              </g>
            );
          })}

          {/* 必修边 (L1→L2→L3→L4) */}
          {BRANCH_ORDER.map((b, branchIdx) => (
            <g key={`prereq-${b}`}>
              {[1, 2, 3].map(lv => {
                const from = cellXY(branchIdx, lv);
                const to = cellXY(branchIdx, lv + 1);
                return (
                  <line
                    key={`p-${b}-${lv}`}
                    x1={from.x + CELL_W}
                    y1={from.y + CELL_H / 2}
                    x2={to.x - 2}
                    y2={to.y + CELL_H / 2}
                    stroke="#f43f5e"
                    strokeWidth="2"
                    opacity="0.6"
                    markerEnd="url(#arrow-prereq)"
                  />
                );
              })}
            </g>
          ))}

          {/* 跨学科桥 (虚线) */}
          {bridges.map((bridge, i) => {
            const [fromBranch, fromLv] = bridge.from.split('.L');
            const [toBranch, toLv] = bridge.to.split('.L');
            const fIdx = BRANCH_ORDER.indexOf(fromBranch);
            const tIdx = BRANCH_ORDER.indexOf(toBranch);
            if (fIdx < 0 || tIdx < 0) return null;
            const f = cellXY(fIdx, parseInt(fromLv));
            const t = cellXY(tIdx, parseInt(toLv));
            return (
              <line
                key={`bridge-${i}`}
                x1={f.x + CELL_W / 2}
                y1={f.y + CELL_H}
                x2={t.x + CELL_W / 2}
                y2={t.y}
                stroke="#22d3ee"
                strokeWidth="1.5"
                strokeDasharray="4 4"
                opacity="0.4"
                markerEnd="url(#arrow-bridge)"
              />
            );
          })}

          {/* Cells */}
          {BRANCH_ORDER.map((b, branchIdx) => (
            LEVEL_ORDER.map(lv => {
              const { x, y } = cellXY(branchIdx, lv);
              const cellId = `${b}.L${lv}`;
              const count = countOf(b, lv);
              const info = LEVEL_INFO[lv];
              const branchColor = CURRICULUM.branches[b].color;
              const isHover = hover === cellId;
              return (
                <g
                  key={cellId}
                  transform={`translate(${x}, ${y})`}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(cellId)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => nav(`/academy/branch/${b.toLowerCase()}?level=${lv}`)}
                >
                  {/* 背景框 */}
                  <rect
                    width={CELL_W}
                    height={CELL_H}
                    rx={10}
                    className={info.color}
                    strokeWidth={isHover ? 2.5 : 1.5}
                    style={{
                      filter: isHover ? 'drop-shadow(0 0 12px rgba(167, 139, 250, 0.6))' : 'none',
                      transition: 'all 0.15s',
                    }}
                  />
                  {/* 标签 */}
                  <text x={CELL_W / 2} y={20} className="fill-slate-100 text-sm font-bold" textAnchor="middle">
                    {info.icon} {levelLabel(lv)}
                  </text>
                  <text x={CELL_W / 2} y={36} className="fill-slate-300 text-xs" textAnchor="middle">
                    {count > 0 ? `${count} ${isZh ? '题' : 'qs'}` : (isZh ? '即将上线' : 'soon')}
                  </text>
                  {/* hover 标签 */}
                  {isHover && (
                    <foreignObject x={-80} y={-50} width={CELL_W + 160} height={50} className="pointer-events-none">
                      <div className="text-center text-xs bg-slate-900/95 px-2 py-1 rounded text-slate-200 border border-violet-500/30">
                        {labelOf(b)} · L{lv} {isZh ? LEVEL_INFO[lv].name_zh : LEVEL_INFO[lv].name_en}
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            })
          ))}
        </svg>
      </div>

      {/* 桥说明 */}
      <div className="mt-6 card bg-cyan-500/5 border-cyan-500/20">
        <h3 className="text-sm font-bold text-cyan-200 mb-2">🌉 {isZh ? '跨学科桥 (10 座)' : 'Cross-discipline bridges (10)'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
          {bridges.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-mono text-cyan-300">{b.from} → {b.to}</span>
              <span className="text-slate-500">·</span>
              <span>{isZh ? b.reason_zh : b.reason_en}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats summary */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {LEVEL_ORDER.map(lv => {
          const info = LEVEL_INFO[lv];
          const count = BRANCH_ORDER.reduce((s, b) => s + countOf(b, lv), 0);
          return (
            <div key={lv} className="card">
              <div className="text-2xl">{info.icon}</div>
              <div className="text-2xl font-bold mt-1">{count}</div>
              <div className="text-xs text-slate-500 mt-1">L{lv} {isZh ? info.name_zh : info.name_en}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
